import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  DateRangePicker,
  PageHeader,
  SelectInput,
  StatsCard,
  Table,
} from '@demandlocal/components'
import { ReactComponent as DownloadIcon } from '../../../../images/icons/action_download.svg'
import { useSearchParams } from 'react-router-dom'
import { useDownloadPDFReport } from '../../../../api/dealership/reports/useDownloadPDFReport'
import toast from 'react-hot-toast'
import { DateRange } from '../../../../lib/types/DateRange'
import { useDateRange } from '../../../../store/zustand/dateRangeStore'
import mtm from '../../../../lib/utils/mtm'
import { GA4 } from '../../../../lib/utils/ga4'
import { useGetGA4Metrics } from '../../../../api/dealership/performance/site/useGetGA4Metrics'
import { formatNumber, formatPercentage } from '../../../../lib/utils/formatNumber'
import HorizontalBarChart from '../../../../components/Charts/HorizontalBarChart'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import MultiLineChart from '../../../../components/Charts/MultiLineChart'
import { useGetGA4Pages } from '../../../../api/dealership/performance/site/useGetGA4Pages'
import { createColumnHelper } from '@tanstack/react-table'
import { FormProvider, useForm } from 'react-hook-form'
import { usePrevious } from '../../../../lib/hooks/usePrevious'
import { debounce, isEmpty, isEqual } from 'lodash'

const pagesColumnHelper = createColumnHelper<{
  page_title: string
  active_users: number
  screen_page_views: number
  event_count: number
  engaged_sessions: number
  sessions: number
  bounce_rate: number
  time_per_session: number
}>()

type FormValues = {
  accounts?: {
    label: string
    value: string
  }
  property?: {
    label: string
    value: string
  }
}

const ReportFilters = ({
  connectedGA4Accounts,
  filters,
  setFilters,
}: {
  connectedGA4Accounts
  filters
  setFilters
}): JSX.Element => {
  const { dateRange } = useDateRange()
  const formContext = useForm<FormValues>()

  const { watch, setValue } = formContext

  const formValues = formContext.watch()
  const accounts = filters.accounts

  const accountOptions = useMemo(
    () =>
      connectedGA4Accounts?.map((acc) => {
        return {
          label: acc.name,
          value: acc.id.split('/')[1],
        }
      }),
    [connectedGA4Accounts],
  )

  const propertyOptions = useMemo(() => {
    return connectedGA4Accounts
      ?.filter((acc) =>
        Number(accounts?.length) > 0 ? accounts.includes(acc.id.split('/')[1]) : acc,
      )
      ?.flatMap((acc) =>
        acc.properties.map((prop) => {
          return {
            label: prop.name,
            value: prop.id.split('/')[1],
          }
        }),
      )
  }, [accounts, connectedGA4Accounts])

  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    if (!isEmpty(filters.accounts) && !isEmpty(accountOptions)) {
      const accounts = searchParams.getAll('accounts')
      if (accounts.length > 0) {
        setValue(
          'accounts',
          accountOptions.filter((option) => accounts.includes(option.value)),
        )
      }
    }
  }, [setValue, setSearchParams, searchParams, accountOptions, filters.accounts])

  useEffect(() => {
    if (!isEmpty(filters.property) && !isEmpty(propertyOptions)) {
      const properties = searchParams.getAll('property')
      if (properties.length > 0) {
        setValue(
          'property',
          propertyOptions.filter((option) => properties.includes(option.value)),
        )
      }
    }
  }, [setValue, setSearchParams, searchParams, filters.property, propertyOptions])

  return (
    <FormProvider {...formContext}>
      <form className={'print:hidden'}>
        <div
          className={
            'p-5 relative rounded-md border-2 bg-primary-persian-blue/5 border-primary-persian-blue/10 mb-8'
          }>
          <div className={'grid grid-cols-3 gap-5'}>
            <SelectInput
              isMulti={true}
              name={'accounts'}
              onChange={(value) => {
                setFilters((prev) => {
                  return {
                    ...prev,
                    accounts: value.map((v) => v.value),
                  }
                })
                setSearchParams((sp) => {
                  if (sp.has('accounts')) sp.delete('accounts')
                  value.forEach((v) => sp.append('accounts', v.value))
                  return sp
                })
              }}
              label={'Accounts'}
              options={accountOptions}
              placeholder="All accounts"
            />
            <SelectInput
              isMulti
              disabled={isEmpty(filters.accounts)}
              name={'property'}
              label={'Property'}
              isClearable
              onChange={(value) => {
                setFilters((prev) => {
                  return {
                    ...prev,
                    property: value.map((v) => v.value),
                  }
                })
                setSearchParams((sp) => {
                  if (sp.has('property')) sp.delete('property')
                  value.forEach((v) => sp.append('property', v.value))
                  return sp
                })
              }}
              options={propertyOptions}
              placeholder="All properties"
            />
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

const GA4Report = (): JSX.Element => {
  const dealership = useDealership()
  const [queryParams, setQueryParams] = useSearchParams()
  const { dateRange, setDateRange } = useDateRange()
  const [isExpanded, setIsExpanded] = useState(false)

  const [filters, setFilters] = useState({
    accounts: queryParams.getAll('accounts') ?? [],
    property: queryParams.getAll('property') ?? [],
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })

  const processedFilters = useMemo(() => {
    return {
      start_date: filters?.dateRange?.startDate,
      end_date: filters?.dateRange?.endDate,
      accounts: filters?.accounts.map((c) => ({ value: c })),
      properties: filters?.property.map((c) => ({ value: c })),
    }
  }, [filters])

  const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
    useDownloadPDFReport()

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  useEffect(() => {
    setQueryParams({
      ...filters.dateRange,
      accounts: filters.accounts,
      property: filters.property,
    })
  }, [filters, setQueryParams])

  const {
    data: {
      connectedGA4Accounts,
      overallMetrics,
      utmMetrics,
      dailyMetrics,
      eventMetrics,
    } = {},
    isLoading,
  } = useGetGA4Metrics({
    filters: {
      ...processedFilters,
    },
  })

  useEffect(() => {
    if (!isLoading) {
      const content = document.getElementById('content')
      if (content) content.className = `${content.className} loaded`
    }
  }, [isLoading])

  const pagesColumns = useMemo(() => {
    return [
      pagesColumnHelper.accessor('page_title', {
        header: 'Page Title',
        cell: (info) => info.getValue(),
      }),
      pagesColumnHelper.accessor('active_users', {
        header: 'Visitors',
        cell: (info) => formatNumber(info.getValue() || 0),
      }),
      pagesColumnHelper.accessor('screen_page_views', {
        header: 'Page Views',
        cell: (info) => formatNumber(info.getValue() || 0),
      }),
      pagesColumnHelper.accessor('event_count', {
        header: 'Events',
        cell: (info) => formatNumber(info.getValue() || 0),
      }),
      pagesColumnHelper.accessor('bounce_rate', {
        header: 'Bounce Rate',
        cell: (info) => formatPercentage(info.getValue() || 0, 1),
      }),
      pagesColumnHelper.accessor('time_per_session', {
        header: 'Avg min/session',
        cell: (info) =>
          formatNumber(info.getValue() / 60 || 0, {
            maximumFractionDigits: 1,
          }),
      }),
    ]
  }, [])

  return (
    <div id={'content'}>
      <PageHeader
        title={`${dealership?.dealership} - GA4`}
        cardsOptions={[]}
        rightSlot={({ hidden }) => (
          <div className={'print:hidden flex flex-row gap-2'}>
            {Number(connectedGA4Accounts?.length ?? 0) > 0 && (
              <Button
                variant={'tertiary'}
                onClick={(e) => {
                  e.preventDefault()
                  setIsExpanded((prev) => !prev)
                }}>
                Filters
              </Button>
            )}
            <DateRangePicker
              collapsed={hidden}
              filters={filters}
              onChange={onDateRangeChange}
            />
            <Button
              disabled={isDownloadingReport}
              collapsed={hidden}
              leftSlot={<DownloadIcon className={'fill-white'} />}
              onClick={async (e) => {
                e.preventDefault()
                const toastId = toast.loading('Generating report...')
                GA4.sendEvent({
                  event_name: 'export_pdf',
                  event_params: {
                    report_type: 'Site',
                  },
                })
                mtm.sendEvent('Export', 'PDF', 'Site')
                await downloadReport(undefined, {
                  async onSuccess(data) {
                    toast.success('Report generated successfully', { id: toastId })
                    const blob = await data.blob()
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `SiteReport_${filters?.dateRange?.startDate}_-_${filters?.dateRange?.endDate}.pdf`
                    document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                    a.click()
                    a.remove()
                  },
                })
              }}>
              Export as PDF
            </Button>
          </div>
        )}
      />
      <div className={'flex flex-1 flex-col p-10 max-w-[1920px] mx-auto w-full'}>
        {isExpanded && (
          <ReportFilters
            filters={filters}
            connectedGA4Accounts={connectedGA4Accounts}
            setFilters={setFilters}
          />
        )}
        <div className={'flex flex-col gap-8'}>
          <div className="grid grid-cols-3 gap-4">
            <StatsCard
              title={'Visitors'}
              content={formatNumber(overallMetrics?.active_users || 0)}
            />
            <StatsCard
              title={'Engaged Sessions'}
              content={formatNumber(overallMetrics?.engaged_sessions || 0)}
            />
            <StatsCard
              title={'New Users'}
              content={formatNumber(overallMetrics?.new_users || 0)}
            />
            <StatsCard
              title={'Page Views'}
              content={formatNumber(overallMetrics?.screen_page_views || 0)}
            />
            <StatsCard
              title={'Pages / Sessions'}
              content={formatNumber(overallMetrics?.pages_per_session || 0, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 1,
              })}
            />
            <StatsCard
              title={'Minutes / Sessions'}
              content={formatNumber((overallMetrics?.time_per_session || 0) / 60 || 0, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className={'min-h-[325px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>UTM Campaigns</span>
              </div>
              <div className={'h-[325px] p-2'}>
                <HorizontalBarChart
                  data={
                    utmMetrics
                      ?.filter((d) => d.type === 'utm_campaign')
                      ?.map((d) => {
                        return {
                          label: d.value,
                          value: d.count,
                        }
                      }) ?? []
                  }
                />
              </div>
            </div>
            <div className={'min-h-[325px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>UTM Sources</span>
              </div>
              <div className={'h-[325px] p-2'}>
                <HorizontalBarChart
                  data={
                    utmMetrics
                      ?.filter((d) => d.type === 'utm_source')
                      ?.map((d) => {
                        return {
                          label: d.value,
                          value: d.count,
                        }
                      }) ?? []
                  }
                />
              </div>
            </div>
            <div className={'min-h-[325px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>UTM Mediums</span>
              </div>
              <div className={'h-[325px] p-2'}>
                <HorizontalBarChart
                  data={
                    utmMetrics
                      ?.filter((d) => d.type === 'utm_medium')
                      ?.map((d) => {
                        return {
                          label: d.value,
                          value: d.count,
                        }
                      }) ?? []
                  }
                />
              </div>
            </div>
          </div>
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <span>Visitors vs Page Views</span>
            </div>
            <div className="h-[275px] p-2">
              <MultiLineChart
                gradient={['blue-to-purple', 'red-to-orange']}
                yKey={'value'}
                data={
                  dailyMetrics
                    ?.sort((a, b) => (a.date > b.date ? 1 : -1))
                    ?.flatMap((data) => {
                      return [
                        {
                          date: data.date,
                          type: 'Visitors',
                          value: data.active_users,
                        },
                        {
                          date: data.date,
                          type: 'Page Views',
                          value: data.screen_page_views,
                        },
                      ]
                    }) ??
                  [] ??
                  []
                }
                groupKey={'type'}
                labelKey={'type'}
                xKey={'date'}
              />
            </div>
          </div>
          <div className={'min-h-[325px] border-2 rounded-xl'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <span>Top Vistor Conversions</span>
            </div>
            <div className="h-[325px] p-2">
              <HorizontalBarChart
                data={
                  eventMetrics
                    // ?.filter(
                    //   (d) =>
                    //     ![
                    //       'page_view',
                    //       'pageview',
                    //       'session_start',
                    //       'first_visit',
                    //       'asc_',
                    //       'user_engagement',
                    //       'scroll',
                    //       'time_on_site',
                    //       'pages_per_session',
                    //     ].some((event) => d.event_name.includes(event)),
                    // )
                    ?.slice(0, 10)
                    ?.map((d) => {
                      return {
                        label: d.event_name,
                        value: d.event_count,
                      }
                    }) ?? []
                }
              />
            </div>
          </div>
          <div className="flex flex-col gap-4">
            <span className="font-bold text-sm">Top Performing Pages</span>
            <Table
              columns={pagesColumns}
              fetchFn={useGetGA4Pages}
              filters={{
                ...processedFilters,
              }}
              defaultSorting={[
                {
                  id: 'active_users',
                  desc: true,
                },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default GA4Report
